import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Button, Select, Spin, Alert } from "antd";
import TextStyles from "../../../components/Elements/TextStyles/TextStyles";
import CardsBorder from "../../../components/Cards/CardsBorder";
import CustomSwitch from "../../../components/Elements/CustomSwitch/CustomSwitch";
import { ReactComponent as DateIcon } from "../../../assets/svgs/date.svg";
import { useDispatch, useSelector } from "react-redux";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import { useLocation } from "react-router-dom";
import {
  fetchCustomerFilteredProjects,
  updateSubAdminLicense,
} from "../../../redux/customer/customerSlice";

const { Option } = Select;

const toDisplayText = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getLicenseTotal = (licenseValue) => {
  if (licenseValue && typeof licenseValue === "object") {
    if (licenseValue.issued !== undefined) return licenseValue.issued;
    if (licenseValue.total !== undefined) return getLicenseTotal(licenseValue.total);
  }

  return licenseValue;
};

const getLicenseDate = (licenseValue, fallback) => {
  if (licenseValue && typeof licenseValue === "object") {
    if (licenseValue.date) return String(licenseValue.date).slice(0, 10);
    if (licenseValue.total && typeof licenseValue.total === "object") {
      return getLicenseDate(licenseValue.total, fallback);
    }
  }

  return fallback;
};

const ProjectLevel = ({ projectId, clientId, onProjectLicenseChange }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isCustomerPage = location.pathname.includes('/customer/customer-page/');
  const { licenses = {} } = useSelector((state) => state.license || {});
  const {
    customerFilteredProjects = [],
    customerFilteredProjectsLoading,
    customerFilteredProjectsError,
    updateSubAdminLicenseLoading,
  } = useSelector((state) => state.customer || {});
  const saving = updateSubAdminLicenseLoading;
  const [popup, setPopup] = useState({ visible: false, type: "success", title: "", message: "" });
  const projectLevelDetails = customerFilteredProjects;

  const [selectedProjectKey, setSelectedProjectKey] = useState(
    projectId || ""
  );

  const selectedProject = useMemo(
    () => projectLevelDetails.find((p) => p._id === selectedProjectKey),
    [selectedProjectKey, projectLevelDetails]
  );

  const [formSettings, setFormSettings] = useState([
    {
      title: "Use of Dynamic Form in the Project",
      description:
        "When Enabled, Customer Can Use Any Form From The OffsetGo Library.",
      date: licenses.dynamic_form_usage_access?.expiry_date || "",
      enabled: licenses.dynamic_form_usage_access?.enabled ?? false,
    },
    {
      title: "Allow The Section-Level Form Assignment",
      description:
        "When Enabled, Customer Can Create Custom Forms Using Pre-Approved Questions.",
      date: licenses.section_level_form_assignment_access?.expiry_date || "",
      enabled: licenses.section_level_form_assignment_access?.enabled ?? false,
    },
  ]);

  // license rows for selected project
  const [customerLevelLicenseData, setCustomerLevelLicenseData] = useState([]);
  const licenseKeys = useMemo(
    () => ["user","employee", "vendor", "local_supervisor"],
    []
  );
  const licenseDisplayNames = {
    user: "User",
    employee: "Employee",
    vendor: "Vendor",
    local_supervisor: "Local Supervisor",

  };

  const fetchProjectList = useCallback(async (preferredProjectId = projectId) => {
    if (!clientId) return;

    const response = await dispatch(
      fetchCustomerFilteredProjects({
        clientId,
        limit: 10,
        skip: 0,
        sort: "-createdAt",
        search: "",
        filterData: "",
      }),
    ).unwrap();

    const projects = response?.data ?? [];
    if (preferredProjectId && projects.some((project) => project._id === preferredProjectId)) {
      setSelectedProjectKey(preferredProjectId);
    } else if (projects[0]?._id) {
      setSelectedProjectKey(projects[0]._id);
    }
  }, [clientId, dispatch, projectId]);

  useEffect(() => {
    fetchProjectList().catch(() => {});
  }, [fetchProjectList]);

  useEffect(() => {
    if (!selectedProject && projectLevelDetails.length > 0) {
      setSelectedProjectKey(projectLevelDetails[0]._id);
    }
  }, [projectLevelDetails, selectedProject]);

  useEffect(() => {
    const approvalLicense = selectedProject?.approval?.data?.[0]?.license;
    const mainLicense = selectedProject?.license;

    if (mainLicense || approvalLicense) {
      const today = new Date().toISOString().slice(0, 10);
      const rows = licenseKeys.map((key) => {
        const approvalValue = approvalLicense?.[key];
        const hasMainValue = mainLicense?.[key] !== undefined && mainLicense?.[key] !== null;
        const mainValue = hasMainValue ? mainLicense[key] : {};
        const displayValue = hasMainValue ? mainValue : approvalValue;
        const issued = toNumber(getLicenseTotal(displayValue));
        const used = toNumber(mainValue?.used);
        const fallbackAvailable =
          mainValue?.unused !== null && mainValue?.unused !== undefined
            ? toNumber(mainValue.unused)
            : Math.max(issued - used, 0);
        const available = fallbackAvailable;
        const date = getLicenseDate(displayValue, getLicenseDate(approvalValue, today));
        const usage = issued > 0 ? `${Math.round((used / issued) * 100)}%` : "0%";

        return {
          licenseType: key,
          issued,
          used,
          available,
          usage,
          date,
          newAmount: issued,
        };
      });
      setCustomerLevelLicenseData(rows);
    } else {
      const today = new Date().toISOString().slice(0, 10);
      setCustomerLevelLicenseData(
        licenseKeys.map((key) => ({
          licenseType: key,
          issued: 0,
          used: 0,
          available: 0,
          usage: "0%",
          date: today,
          newAmount: 0,
        }))
      );
    }
  }, [selectedProject, licenseKeys]);

  useEffect(() => {
    // Prefer approval data for the project license total
    const approvalSubAdmin = selectedProject?.approval?.data?.[0]?.license?.sub_admin;
    let projectLicenseTotal = 0;

    if (approvalSubAdmin !== undefined && approvalSubAdmin !== null) {
      projectLicenseTotal = toNumber(getLicenseTotal(approvalSubAdmin));
    } else {
      const totalField = selectedProject?.license?.sub_admin?.total;
      projectLicenseTotal = toNumber(getLicenseTotal(totalField));
    }

    if (typeof onProjectLicenseChange === "function") {
      onProjectLicenseChange(projectLicenseTotal);
    }
  }, [selectedProject, onProjectLicenseChange]);

  const handleInputChange = (index, field, value) => {
    const updatedData = [...customerLevelLicenseData];
    updatedData[index][field] = value;
    setCustomerLevelLicenseData(updatedData);
  };

  const handleLicenseAmountChange = (index, value) => {
    const amount = Number(value) || 0;
    const updatedData = [...customerLevelLicenseData];
    const used = Number(updatedData[index].used) || 0;
    updatedData[index] = {
      ...updatedData[index],
      newAmount: amount,
      issued: amount,
      available: amount - used,
      usage: amount > 0 ? `${Math.round((used / amount) * 100)}%` : "0%",
    };
    setCustomerLevelLicenseData(updatedData);
  };

  const showPopup = (type, title, message) => {
    setPopup({ visible: true, type, title, message });
    setTimeout(() => setPopup((prev) => ({ ...prev, visible: false })), 3000);
  };

  const handleSave = async () => {
    if (!selectedProject?._id) return;

    // Send only the newAmount number for each license key: { sub_admin: 5 }
    const licensePayload = customerLevelLicenseData.reduce((acc, row) => {
      const key = String(row.licenseType || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_");

      if (key) {
        acc[key] = row.newAmount}
      return acc;
    }, {});

    const payload = {
      // standard_form: {
      //   checked: selectedProject?.standard_form || [],
      //   unchecked: [],
      // },
      license: licensePayload,
      // dynamic_form_access: selectedProject?.dynamic_form_access ?? false,
      // section_access: selectedProject?.section_access ?? false,
      // valid_till_end_date: selectedProject?.valid_till_end_date || null,
    };

    try {
      await dispatch(
        updateSubAdminLicense({
          projectId: selectedProject._id,
          data: payload,
        }),
      ).unwrap();
      showPopup("success", "Success", "Project license saved successfully");
    } catch (error) {
      console.error("Failed to save project setup", error);
      showPopup("error", "Error", error?.message || "Failed to save project setup");
    }
  };

  return (
    <div className="mx-4">
      <TextStyles color="gradient" style={{ fontSize: "20px", fontWeight: 500 }}>
        <p className="">PROJECT LEVEL LICENSE & FEATURES</p>
      </TextStyles>

      <p className="font-light">
        Select a project to manage its licences and enable/disable features
      </p>

      <TextStyles color="gradient" style={{ fontSize: "20px", fontWeight: 500 }}>
        <p className="">SELECT PROJECT</p>
      </TextStyles>

      {customerFilteredProjectsLoading ? (
        <div className="py-6 flex justify-center">
          <Spin />
        </div>
      ) : customerFilteredProjectsError ? (
        <Alert type="error" message={customerFilteredProjectsError} showIcon />
      ) : (
        <Select
          value={selectedProjectKey || undefined}
          onChange={(value) => setSelectedProjectKey(value)}
          placeholder="Select Project"
          className="w-full"
          size="large"
          bordered
          style={{
            border: "1px solid #06513D",
            borderRadius: "0.5rem",
            color: "#06513D",
            fontSize: "1.125rem",
            fontWeight: 500,
          }}
        >
          {projectLevelDetails.map((project) => (
            <Option key={project._id} value={project._id}>
              {project.name}
            </Option>
          ))}
        </Select>
      )}

      {/* Project info */}
      {selectedProject && (
        <div className="w-full bg-[#E6EEEC] text-[#1F2A37] rounded-lg my-4">
          <div className="py-6 px-6">
            <p className="font-semibold">{selectedProject.name}</p>
            <p className="text-sm text-gray-600 mt-1">
              Type: {toDisplayText(selectedProject.project_type)}
            </p>
            <div className="flex gap-6 font-light text-[13px] mt-2">
              <div className="flex gap-2">
                <div className="pt-[2px]">
                  <DateIcon />
                </div>
                Start Date:{" "}
                {selectedProject.startDate
                  ? new Date(selectedProject.startDate).toLocaleDateString(
                      "en-US",
                      {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      }
                    )
                  : "N/A"}
              </div>
              <div className="flex gap-2">
                <div className="pt-[2px]">
                  <DateIcon />
                </div>
                End Date:{" "}
                {selectedProject.valid_till_end_date
                  ? new Date(selectedProject.valid_till_end_date).toLocaleDateString(
                      "en-US",
                      {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      }
                    )
                  : "N/A"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* License Table */}
      <CardsBorder>
        <div className="rounded-md">
          <table className="w-full text-left">
            <thead className="bg-[#E6EEEC] text-sm font-semibold text-[#1F2A37]">
              <tr>
                <th className="px-2 py-2 text-center">LICENSE TYPE</th>
                <th className="px-2 py-2 text-center">ISSUED</th>
                <th className="px-2 py-2 text-center">USED</th>
                <th className="px-2 py-2 text-center">AVAILABLE</th>
                <th className="px-2 py-2 text-center">USAGE</th>
                <th className="px-2 py-2 text-center">DATE</th>
                <th className="px-2 py-2 text-center">NEW VALUE</th>
              </tr>
            </thead>
            <tbody className="text-sm text-black">
              {customerLevelLicenseData.map((row, index) => (
                <tr key={index} className="border-b border-gray-300 text-center">
                  <td className="px-2 py-3">{licenseDisplayNames[row.licenseType] || toDisplayText(row.licenseType)}</td>
                  <td className="px-2">{toDisplayText(row.issued, "0")}</td>
                  <td className="px-2">{toDisplayText(row.used, "0")}</td>
                  <td className="px-2">{toDisplayText(row.available, "0")}</td>
                  <td className="px-2">
                    <div className="w-full bg-gray-200 rounded h-2">
                      <div
                        className="bg-[#06513D] h-2 rounded"
                        style={{ width: toDisplayText(row.usage, "0%") }}
                      />
                    </div>
                    <span className="text-xs">{toDisplayText(row.usage, "0%")}</span>
                  </td>
                  <td className="px-2">
                    <input
                      type="date"
                      value={toDisplayText(row.date, "")}
                      onChange={(e) =>
                        handleInputChange(index, "date", e.target.value)
                      }
                      className="border border-black rounded px-2 py-1 w-[130px]"
                    />
                  </td>
                  <td className="px-2">
                    <input
                      type="number"
                      min="1"
                      value={row.newAmount === 0 ? "" : toDisplayText(row.newAmount, "")}
                      onChange={(e) =>
                        handleLicenseAmountChange(index, e.target.value)
                      }
                      placeholder="0"
                      className="border border-black rounded px-2 py-1 w-[60px]"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!isCustomerPage && (
            <div className="text-center py-5">
              <Button
                type="primary"
                style={{ background: "#06513D" }}
                className="text-sm font-bold px-6 py-5"
                loading={saving}
                onClick={handleSave}
              >
                Save Change
              </Button>
            </div>
          )}
        </div>
      </CardsBorder>

      {/* Project Features */}
      {/* <TextStyles color="gradient" style={{ fontSize: "20px", fontWeight: 500 }}>
        <p className="mx-4 mt-6"> PROJECT FEATURES</p>
      </TextStyles>

      <CardsBorder>
        {formSettings.map((setting, index) => (
          <div key={index}>
            <div className="flex justify-between mx-10">
              <div>
                <p className="text-lg font-[300]">{setting.title}</p>
                <p className="mt-[-10px] text-[13px] text-[#808080] font-[400] ">
                  {setting.description}
                </p>
              </div>
              <div className="flex justify-around gap-20 items-center">
                <input
                  type="date"
                  value={setting.date}
                  onChange={(e) => {
                    const updated = [...formSettings];
                    updated[index].date = e.target.value;
                    setFormSettings(updated);
                  }}
                  className="border border-black rounded px-2 py-1 w-[120px] text-right"
                />
                <CustomSwitch
                  checked={setting.enabled}
                  onChange={(val) => {
                    const updated = [...formSettings];
                    updated[index].enabled = val;
                    setFormSettings(updated);
                  }}
                />
              </div>
            </div>

            <div className="pb-3 px-4">
              <hr />
            </div>
          </div>
        ))}

        {!isCustomerPage && (
          <div className="text-center py-5">
            <Button
              type="primary"
              style={{ background: "#06513D" }}
              className="text-sm font-bold px-6 py-5"
              loading={saving}
              onClick={handleSave}
            >
              Save Change
            </Button>
          </div>
        )}
      </CardsBorder> */}
      {/* Popup Notification */}
      {popup.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
            onClose={() => setPopup((prev) => ({ ...prev, visible: false }))}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectLevel;
